<script lang="ts">
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import Icon from "../Icon.svelte";
	import trash from "../../../../core/assets/icons/trash.svg?raw";
	import type { Empty } from "../../../../core/utils/Empty.js";
	import type { TooltipImage } from "./ProductConfiguratorOptionProps.js";
	import Link from "../Link.svelte";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import type { OpenableOption } from "./OpenableOption.js";
	import { frequentlyAskedQuestionsPathMap } from "../../../../core/schema/paths/frequentlyAskedQuestionsPathMap.js";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";
	import Tooltip from "../Tooltip.svelte";
	import { requiredProductOptions } from "../../../../core/schema/requiredProductOptions.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import type { ProductSize } from "../../../../core/schema/ProductSize.js";
	import type { ProductOptions } from "../../../../core/schema/ProductOptions.js";
	import { productSizeKeyMap } from "../../../../core/schema/productSizeKeyMap.js";

	export let id: string;
	export let name: string;
	export let minimalPricesForOptions: Record<ProductOption, number>;
	export let pricesForOptions: Record<ProductOption, number>;
	export let minimalPriceIsFinal = false;
	export let image: string | undefined = undefined;
	export let enabled = false;
	export let icon: string;
	export let tooltipCharacter: string | undefined = "?";
	export let cancelChanges: () => void = () => true;
	export let productOption: OpenableOption;
	export let openedOption: OpenableOption | undefined = undefined;
	export let product: ProductForCustomer;
	export let selectedSize: ProductSize;
	/**
	 * `tooltipImages` are currently not used. Kept for future possible use.
	 */
	export const tooltipImages: TooltipImage[] = [];

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface $$Slots {
		tooltip: Empty;
		drawer: Empty;
	}

	const { TENANT_LOCALE } = getGlobalSettings();

	function disable(event: Event): void {
		event.preventDefault();
		enabled = false;
		cancelChanges();
		openedOption = undefined;
	}

	let input: HTMLInputElement | null = null;

	$: {
		if (input) {
			if (input.checked !== enabled) {
				input.checked = enabled;
				input.dispatchEvent(new Event("change", { bubbles: true }));
				input.dispatchEvent(new Event("input", { bubbles: true }));
			}
		}
	}

	$: price =
		productOption !== "photos"
			? pricesForOptions[productOption]
			: pricesForOptions[ProductOption.StandingPhotos] + pricesForOptions[ProductOption.LyingPhoto];
	$: minimalPrice =
		productOption !== "photos"
			? minimalPricesForOptions[productOption]
			: Math.min(
					minimalPricesForOptions[ProductOption.StandingPhotos],
					minimalPricesForOptions[ProductOption.LyingPhoto],
				);

	$: required =
		productOption !== "photos"
			? requiredProductOptions.includes(productOption)
			: [ProductOption.LyingPhoto, ProductOption.StandingPhotos].some((option) =>
					requiredProductOptions.includes(option),
				);

	function onInvalid(event: Event): void {
		const input = event.target as HTMLInputElement;
		input.setCustomValidity("Tato možnost je povinná");
	}

	function isOpenableOptionEnabledForSize(
		productOptions: ProductOptions,
		size: ProductSize,
		option: OpenableOption,
	): boolean {
		const optionsInSize = productOptions[productSizeKeyMap[size]];
		if (
			option === "photos" &&
			(optionsInSize.includes(ProductOption.LyingPhoto) || optionsInSize.includes(ProductOption.StandingPhotos))
		) {
			return true;
		}
		return (optionsInSize as OpenableOption[]).includes(option);
	}

	$: disabledForSelectedSize = !isOpenableOptionEnabledForSize(product.enabledOptions, selectedSize, productOption);

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<div class="relative flex rounded-lg">
	{#if required && !enabled && openedOption === undefined}
		<input class="pointer-events-none absolute inset-0 opacity-0" type="text" required on:invalid={onInvalid} />
	{/if}

	<label
		class="flex w-full cursor-pointer flex-col items-center justify-start gap-3 rounded-lg {enabled
			? 'border-secondary border-2 px-2 py-3 sm:p-3'
			: 'border-grey-200 border px-[calc(.5rem+1px)] py-[calc(.75rem+1px)] sm:p-[calc(.75rem+1px)]'}"
		for={id}
	>
		<div class="flex w-full cursor-pointer items-center gap-2 {disabledForSelectedSize ? 'text-gray-300' : ''}">
			<input
				bind:this={input}
				type="checkbox"
				class="hidden"
				{id}
				name={id}
				disabled={disabledForSelectedSize}
				bind:checked={enabled}
				readonly={enabled}
				on:click={(event) => {
					if (disabledForSelectedSize) {
						return;
					}
					openedOption = productOption;
					if (enabled || $$slots.drawer) {
						event.preventDefault();
					}
				}}
			/>
			<div class="relative">
				<Icon {icon} class="aspect-square h-5 sm:h-7" />
			</div>
			<div class="flex gap-2">
				{#if image}
					<StaticPicture {image} alt={name} width={32} height={32} class="aspect-square w-8" />
				{/if}
				<Tooltip
					class="relative pr-4 text-xs leading-[1.2] transition-all duration-200 after:pointer-events-none after:absolute after:-left-12 after:bottom-full after:h-4 after:w-full after:min-w-[18rem] after:content-[''] hover:after:pointer-events-auto sm:text-base {enabled
						? 'font-semibold'
						: ''}"
				>
					<span>{name}</span>
					{#if $$slots.tooltip}
						<span
							class="bg-grey-200 absolute right-0 top-0 ml-0.5 grid aspect-square h-3 place-items-center rounded-full text-[9px] font-bold"
						>
							{tooltipCharacter}
						</span>
					{/if}

					<div slot="tooltip">
						{#if disabledForSelectedSize}
							<div class="flex max-w-64 flex-col gap-[.875rem] !font-normal">
								{nbspify("Produkt s tímto příplatkem se ve vybrané velikosti nevyrábí.")}
							</div>
						{:else}
							<div class="flex max-w-64 flex-col gap-[.875rem] !font-normal">
								<slot name="tooltip" />
								<p>
									Více informací v sekci
									<Link href="/{frequentlyAskedQuestionsPathMap[TENANT_LOCALE]}">časté dotazy</Link>.
								</p>
							</div>
						{/if}
					</div>
				</Tooltip>
			</div>
			<div class="flex flex-1 gap-2 sm:gap-1">
				<span
					class="flex flex-1 justify-end text-xs transition-all duration-200 sm:text-base"
					class:font-semibold={enabled}
				>
					{#if price === 0 && minimalPrice === 0 && minimalPriceIsFinal}
						zdarma
					{:else if (!enabled || price === 0) && !minimalPriceIsFinal}
						od&nbsp;{nbspify(formatPrice(minimalPrice, TENANT_LOCALE, false))}
					{:else if minimalPriceIsFinal && price === 0}
						+&nbsp;{nbspify(formatPrice(minimalPrice, TENANT_LOCALE, false))}
					{:else}
						+&nbsp;{nbspify(formatPrice(price, TENANT_LOCALE, false))}
					{/if}
				</span>
				{#if enabled}
					<button type="button" on:click={disable}>
						<Icon
							icon={trash}
							class="hover:text-secondary-300 text-secondary-250 aspect-square h-4 transition-colors duration-300"
						/>
					</button>
				{/if}
			</div>
		</div>
	</label>
</div>

<slot name="drawer" />
